import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Collapse, Table, Input, InputNumber, DatePicker, Button, Space, message, Select, Popconfirm } from "antd";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { serverFetch } from "../../server";
import ServiceExcelClients from "./service-excel-clients";
import ServiceExcelProducts from "./service-excel-products";
import ServiceExcelSales from "./service-excel-sales";
import "./service-excel.css";

const ServiceExcel = () => {
  const [activeKeys, setActiveKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientsCount, setClientsCount] = useState(0);
  const [productsCount, setProductsCount] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [clientsValidation, setClientsValidation] = useState({ errors: 0, warnings: 0 });
  const [productsValidation, setProductsValidation] = useState({ errors: 0, warnings: 0 });
  const [salesValidation, setSalesValidation] = useState({ errors: 0, warnings: 0 });
  const clientsRef = useRef(null);
  const productsRef = useRef(null);
  const salesRef = useRef(null);
  const fileInputRef = useRef(null);

  // Обновление счетчиков валидации
  const updateValidationCounts = useCallback(() => {
    const clientsCounts = clientsRef.current?.getValidationCounts?.() || { errors: 0, warnings: 0 };
    const productsCounts = productsRef.current?.getValidationCounts?.() || { errors: 0, warnings: 0 };
    const salesCounts = salesRef.current?.getValidationCounts?.() || { errors: 0, warnings: 0 };
    
    setClientsValidation(clientsCounts);
    setProductsValidation(productsCounts);
    setSalesValidation(salesCounts);
  }, []);

  // Используем useEffect для обновления счетчиков при изменении количества записей и периодически
  useEffect(() => {
    updateValidationCounts();
    const interval = setInterval(updateValidationCounts, 1000);
    return () => clearInterval(interval);
  }, [clientsCount, productsCount, salesCount, updateValidationCounts]);

  // Мемоизируем массив items для Collapse
  const collapseItems = useMemo(() => [
    {
      key: "clients",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src="/images/user_plus_outline.svg"
            alt="add"
            onClick={(e) => {
              e.stopPropagation();
              clientsRef.current?.addRow?.();
            }}
            style={{ cursor: "pointer", width: 20, height: 20 }}
          />
          <span>
            Клиенты ({clientsCount}
            {clientsValidation.errors > 0 && <span style={{ color: '#ff4d4f' }}> ⚠ {clientsValidation.errors}</span>}
            {clientsValidation.warnings > 0 && <span style={{ color: '#d48806' }}> ⚠ {clientsValidation.warnings}</span>})
          </span>
        </div>
      ),
      children: (
        <ServiceExcelClients 
          ref={clientsRef} 
          onClientsChange={(count) => {
            setClientsCount(count);
          }}
        />
      ),
      forceRender: true
    },
    {
      key: "nomenclature",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src="/images/user_plus_outline.svg"
            alt="add"
            onClick={(e) => {
              e.stopPropagation();
              productsRef.current?.addRow?.();
            }}
            style={{ cursor: "pointer", width: 20, height: 20 }}
          />
          <span>
            Номенклатура ({productsCount}
            {productsValidation.errors > 0 && <span style={{ color: '#ff4d4f' }}> ⚠ {productsValidation.errors}</span>}
            {productsValidation.warnings > 0 && <span style={{ color: '#d48806' }}> ⚠ {productsValidation.warnings}</span>})
          </span>
        </div>
      ),
      children: (
        <ServiceExcelProducts 
          ref={productsRef} 
          onProductsChange={(count) => {
            setProductsCount(count);
          }}
        />
      ),
      forceRender: true
    },
    {
      key: "abonements",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src="/images/user_plus_outline.svg"
            alt="add"
            onClick={(e) => {
              e.stopPropagation();
              salesRef.current?.addRow?.();
            }}
            style={{ cursor: "pointer", width: 20, height: 20 }}
          />
          <span>
            Абонементы клиентов ({salesCount}
            {salesValidation.errors > 0 && <span style={{ color: '#ff4d4f' }}> ⚠ {salesValidation.errors}</span>}
            {salesValidation.warnings > 0 && <span style={{ color: '#d48806' }}> ⚠ {salesValidation.warnings}</span>})
          </span>
        </div>
      ),
      children: (
        <ServiceExcelSales 
          ref={salesRef} 
          onSalesChange={(count) => {
            setSalesCount(count);
          }}
          clientsRef={clientsRef}
          productsRef={productsRef}
        />
      ),
      forceRender: true
    }
  ], [clientsCount, productsCount, salesCount, clientsValidation, productsValidation, salesValidation]);



  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Ошибка чтения файла"));
      reader.readAsArrayBuffer(file);
    });
  };


  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      message.error("Пожалуйста, выберите файл Excel (.xls или .xlsx)");
      event.target.value = null;
      return;
    }

    setLoading(true);
    try {
      // Очищаем все таблицы перед загрузкой
      clientsRef.current?.clearClients();
      productsRef.current?.clearProducts();
      salesRef.current?.clearSales();

      const workbook = await parseExcelFile(file);
      const sheetNames = workbook.SheetNames;

      // Ищем листы по названиям (гибкий поиск)
      const clientsSheet = sheetNames.find(name => {
        const lowerName = name.toLowerCase().trim();
        return lowerName.includes("клиент") || lowerName === "клиенты" || lowerName === "client";
      });
      const nomenclatureSheet = sheetNames.find(name => {
        const lowerName = name.toLowerCase().trim();
        return lowerName.includes("номенклатур") || lowerName === "номенклатура" || lowerName === "nomenclature";
      });
      const abonementsSheet = sheetNames.find(name => {
        const lowerName = name.toLowerCase().trim();
        return lowerName.includes("абонемент") || lowerName.includes("абонементы") || lowerName === "abonement" || lowerName === "abonements";
      });

      // Парсим данные из листов
      if (clientsSheet) {
        const worksheet = workbook.Sheets[clientsSheet];
        const count = clientsRef.current?.loadFromExcel(worksheet) || 0;
        message.success(`Загружено ${count} записей из листа "${clientsSheet}"`);
      }

      if (nomenclatureSheet) {
        const worksheet = workbook.Sheets[nomenclatureSheet];
        const count = productsRef.current?.loadFromExcel(worksheet) || 0;
        message.success(`Загружено ${count} записей из листа "${nomenclatureSheet}"`);
      }

      if (abonementsSheet) {
        const worksheet = workbook.Sheets[abonementsSheet];
        const count = salesRef.current?.loadFromExcel(worksheet) || 0;
        message.success(`Загружено ${count} записей из листа "${abonementsSheet}"`);
      }

      if (!clientsSheet && !nomenclatureSheet && !abonementsSheet) {
        message.warning("Не найдены листы с названиями: Клиенты, Номенклатура, Абонементы клиентов");
      }

    } catch (error) {
      console.error("Ошибка при обработке файла:", error);
      message.error("Ошибка при чтении файла Excel: " + (error.message || "Неизвестная ошибка"));
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };






  // Проверка наличия критических ошибок
  const hasCriticalErrors = () => {
    // Проверка клиентов
    if (clientsRef.current?.hasCriticalErrors()) return true;

    // Проверка номенклатуры
    if (productsRef.current?.hasCriticalErrors()) return true;

    // Проверка абонементов
    if (salesRef.current?.hasCriticalErrors()) return true;

    return false;
  };

  // Проверка наличия записей для сохранения
  const hasRecordsToSave = () => {
    return clientsCount > 0 || productsCount > 0 || salesCount > 0;
  };

  // Проверка наличия ошибок валидации (критических ошибок)
  const hasAnyErrors = () => {
    return clientsValidation.errors > 0 || productsValidation.errors > 0 || salesValidation.errors > 0;
  };

  // Преобразование данных для отправки
  const prepareDataForSave = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      const date = dayjs(dateStr);
      return date.isValid() ? date.format("YYYY-MM-DD") : null;
    };

    const clientsData = clientsRef.current?.getClientsData() || [];
    const productsData = productsRef.current?.getProductsData() || [];
    const salesData = salesRef.current?.getSalesData() || [];

    return {
      Clients: clientsData,
      Products: productsData,
      Sales: salesData
    };
  };

  // Сохранение данных
  const handleSave = async () => {
    if (!hasRecordsToSave()) {
      message.warning("Нет записей для сохранения");
      return;
    }
    
    if (hasAnyErrors()) {
      message.error("Исправьте ошибки перед сохранением");
      return;
    }

    setLoading(true);
    try {
      const data = prepareDataForSave();
      const response = await serverFetch("extranet/import", {
        method: "POST",
        bodyData: data
      });

      setLoading(false);
      
      // Если есть ошибка в ответе
      if (response.error) {
        message.error(response.error);
        return;
      }

      // Отображаем результат импорта
      if (response) {
        message.success("Данные успешно сохранены");
        // Можно отобразить детали результата, если они есть
        if (response.Clients) {
          console.log("Импортировано клиентов:", response.Clients);
        }
        if (response.Products) {
          console.log("Импортировано номенклатуры:", response.Products);
        }
        if (response.Sales) {
          console.log("Импортировано продаж:", response.Sales);
        }
      }
    } catch (error) {
      setLoading(false);
      message.error(error.userMessage || error.message || "Ошибка при сохранении данных");
    }
  };

  const clearAllTables = () => {
    clientsRef.current?.clearClients();
    productsRef.current?.clearProducts();
    salesRef.current?.clearSales();
    message.success("Все таблицы очищены");
  };



  return (
    <div className="col-one-main">
      <div className="service-excel-header">
        {/* <div className="col-one-title service-excel-header-title">Импорт Excel</div> */}
        <div className="service-excel-header-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            onChange={handleFileChange}
            disabled={loading}
            className="service-excel-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            loading={loading}
          >
            Выбрать файл
          </Button>
          <Button
            onClick={clearAllTables}
            disabled={loading}
          >
            Очистить
          </Button>
          <Popconfirm
            title="Сохранить данные в базу?"
            description="Вы уверены, что хотите сохранить все данные?"
            onConfirm={handleSave}
            okText="Да"
            cancelText="Нет"
            disabled={loading || !hasRecordsToSave() || hasAnyErrors()}
          >
            <Button
              type="primary"
              disabled={loading || !hasRecordsToSave() || hasAnyErrors()}
              loading={loading}
            >
              Сохранить
            </Button>
          </Popconfirm>
        </div>
      </div>
      <Collapse
        activeKey={activeKeys}
        onChange={setActiveKeys}
        style={{ marginBottom: 16 }}
        items={collapseItems}
      />
    </div>
  );
}

export default ServiceExcel;
